import React from 'react';
import { useAsyncValue } from '../../model/data';
import { Icon } from '../Icon/Icon';
import { t } from '../../locales/translate';
import { SeatStatus, VenueLayout } from '../../model/venue';
import { loadSeatWithId } from '../../model/contingent';
import { useErrorHandler } from '../../util/util';
import { Link } from 'react-router-dom';
import { orderPath } from '../../routes';
import { useIsAdmin } from '../../model/membership';
import { Tag } from '../Tag/Tag';
import { Seat, Section } from '../../model/seatmap/types';

interface Props {
  eventId?: string;
  venueLayout?: VenueLayout;
  section: Section | null;
  seat: Seat | null;
}

export function InfoOverlay({ seat, section, eventId }: Props): JSX.Element | null {
  const admin = useIsAdmin();
  const status = seat?.allocation?.status;
  const blockReason = seat?.allocation?.blockReason;
  const permanentBlocked = seat?.allocation?.permanent;
  const itemId = seat?.allocation?.itemId;
  const pricingCategory = seat?.pricingCategory ?? section?.pricingCategory;
  const showOrder = admin && status === 'sold' && itemId;

  return (
    <div>
      {pricingCategory && <div>{pricingCategory.name}</div>}
      <div>
        {showOrder && eventId && <OrderNode itemId={itemId} event={{ id: eventId }} />}
        {blockReason && (
          <div>
            {t.model.status.blocked}: <span className="semibold">{blockReason}</span>
          </div>
        )}
        {permanentBlocked && <Tag text={t.model.status.permanent} backgroundColor="var(--color-tint)" />}
      </div>
    </div>
  );
}

const OrderNode = ({ itemId, event }: { itemId: string; event: { id: string } }): JSX.Element | null => {
  const [seatStatus, loading, error] = useAsyncValue<
    SeatStatus & {
      orderId?: string;
      orderReference?: string;
    }
  >(() => loadSeatWithId(itemId, event.id), [itemId]);
  useErrorHandler(error);

  if (loading) {
    return <Icon loading name="plus" />;
  }
  if (!seatStatus?.orderId) {
    return null;
  }

  return (
    <Link className="link" type="link" to={orderPath(seatStatus.orderId)}>
      {seatStatus.orderReference}
    </Link>
  );
};
